import { Spin } from 'antd';
import React, { useLayoutEffect, useState } from 'react';

import { appRoutes, navigateTo, type AppRouteKey } from '@/app/routes';
import { AssessmentStep2Generating } from '@/components/assessment/AssessmentStep2Generating';
import { AssessmentStep2Review } from '@/components/assessment/AssessmentStep2Review';
import type { AssessmentFlowStep } from '@/components/assessment/AssessmentStepPager';
import { InterviewGuideStep } from '@/components/assessment/InterviewGuideStep';
import { InterviewPrep } from '@/components/assessment/InterviewPrep';
import { StepEditor } from '@/components/assessment/StepEditor';
import { StepGenerating } from '@/components/assessment/StepGenerating';
import { StepOutput } from '@/components/assessment/StepOutput';
import { TranscriptAnalysisStep } from '@/components/assessment/TranscriptAnalysisStep';

const LegacyProjectStep1Redirect: React.FC<{
  projectId: string;
  clientId: string;
}> = ({ projectId, clientId }) => {
  useLayoutEffect(() => {
    const key = `bdp.legacyStep1.${clientId}.${projectId}`;
    let instanceId: string | null = null;
    try {
      instanceId = sessionStorage.getItem(key);
    } catch {
      // ignore
    }
    if (!instanceId) {
      instanceId = globalThis.crypto.randomUUID();
      try {
        sessionStorage.setItem(key, instanceId);
      } catch {
        // ignore
      }
    }
    navigateTo(appRoutes.projectAssessmentStep1(instanceId, projectId, clientId), true);
  }, [projectId, clientId]);
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
      <Spin size="large" />
    </div>
  );
};

export interface InterviewPageRouterProps {
  routeKey: AppRouteKey;
  assessmentInstanceId: string | undefined;
  effectiveClientId: string;
  effectiveProjectId: string;
  accessToken: string | null;
  microsoftOboAssertion: string | null;
  projectSharepointReady: boolean | null;
  clientLabel: string;
  assessmentLabel: string;
  onStartSso: () => Promise<void>;
  onRefreshMicrosoftObo: () => Promise<string | null>;
  onNavigateAssessmentStep: (instanceId: string, step: AssessmentFlowStep) => void;
}

export const InterviewPageRouter: React.FC<InterviewPageRouterProps> = ({
  routeKey,
  assessmentInstanceId,
  effectiveClientId,
  effectiveProjectId,
  accessToken,
  microsoftOboAssertion,
  projectSharepointReady,
  clientLabel,
  assessmentLabel,
  onStartSso,
  onRefreshMicrosoftObo,
  onNavigateAssessmentStep,
}) => {
  const [step2Counts, setStep2Counts] = useState({ transcriptCount: 0, documentCount: 0 });

  switch (routeKey) {
    case 'step-1':
      if (assessmentInstanceId) {
        const instanceId = assessmentInstanceId;
        return (
          <InterviewPrep
            clientLabel={clientLabel}
            projectAssessmentLabel={assessmentLabel}
            assessmentInstanceId={instanceId}
            sharepointReady={projectSharepointReady}
            accessToken={accessToken}
            microsoftOboAssertion={microsoftOboAssertion}
            clientId={effectiveClientId}
            projectId={effectiveProjectId}
            onMicrosoftReauth={onStartSso}
            onMicrosoftRefreshObo={onRefreshMicrosoftObo}
            onNavigateAssessmentStep={(step) => onNavigateAssessmentStep(instanceId, step)}
            onRunGenerate={() =>
              navigateTo(
                appRoutes.projectAssessmentStep1Generating(
                  instanceId,
                  effectiveProjectId,
                  effectiveClientId,
                ),
              )
            }
          />
        );
      }
      return (
        <LegacyProjectStep1Redirect projectId={effectiveProjectId} clientId={effectiveClientId} />
      );
    case 'step-1-generating':
      if (assessmentInstanceId) {
        const instanceId = assessmentInstanceId;
        return (
          <StepGenerating
            stepNumber={1}
            assessmentInstanceId={instanceId}
            accessToken={accessToken}
            clientId={effectiveClientId}
            projectId={effectiveProjectId}
            clientLabel={clientLabel}
            onScopedSuccess={() =>
              navigateTo(
                appRoutes.projectAssessmentStep1Review(
                  instanceId,
                  effectiveProjectId,
                  effectiveClientId,
                ),
              )
            }
            onScopedFailure={() =>
              navigateTo(
                appRoutes.projectAssessmentStep1(instanceId, effectiveProjectId, effectiveClientId),
              )
            }
          />
        );
      }
      return (
        <StepGenerating
          stepNumber={1}
          clientLabel={clientLabel}
          onOpenOutput={() =>
            navigateTo(appRoutes.step1Output(effectiveProjectId, effectiveClientId))
          }
        />
      );
    case 'step-1-output':
      if (assessmentInstanceId) {
        const instanceId = assessmentInstanceId;
        return (
          <StepOutput
            stepNumber={1}
            onBackToStep={() =>
              navigateTo(
                appRoutes.projectAssessmentStep1(instanceId, effectiveProjectId, effectiveClientId),
              )
            }
            onContinue={() =>
              navigateTo(
                appRoutes.projectAssessmentStep2(instanceId, effectiveProjectId, effectiveClientId),
              )
            }
          />
        );
      }
      return (
        <StepOutput
          stepNumber={1}
          onBackToStep={() => navigateTo(appRoutes.step1(effectiveProjectId, effectiveClientId))}
          onContinue={() => navigateTo(appRoutes.step2(effectiveProjectId, effectiveClientId))}
        />
      );
    case 'step-1-review':
      if (assessmentInstanceId) {
        const instanceId = assessmentInstanceId;
        return (
          <InterviewGuideStep
            clientLabel={clientLabel}
            projectAssessmentLabel={assessmentLabel}
            assessmentInstanceId={instanceId}
            accessToken={accessToken}
            clientId={effectiveClientId}
            projectId={effectiveProjectId}
            currentFlowStep="1-review"
            onNavigateAssessmentStep={(step) => onNavigateAssessmentStep(instanceId, step)}
          />
        );
      }
      return (
        <StepOutput
          stepNumber={1}
          onBackToStep={() => navigateTo(appRoutes.step1(effectiveProjectId, effectiveClientId))}
          onContinue={() => navigateTo(appRoutes.step2(effectiveProjectId, effectiveClientId))}
        />
      );
    case 'step-2':
      if (assessmentInstanceId) {
        const instanceId = assessmentInstanceId;
        return (
          <TranscriptAnalysisStep
            clientLabel={clientLabel}
            projectAssessmentLabel={assessmentLabel}
            assessmentInstanceId={instanceId}
            accessToken={accessToken}
            clientId={effectiveClientId}
            projectId={effectiveProjectId}
            sharepointReady={projectSharepointReady}
            microsoftOboAssertion={microsoftOboAssertion}
            currentFlowStep={2}
            onNavigateAssessmentStep={(step) => onNavigateAssessmentStep(instanceId, step)}
            onWorkflowStart={(_runId, _streamUrl, counts) => {
              setStep2Counts(counts);
              navigateTo(
                appRoutes.projectAssessmentStep2Generating(
                  instanceId,
                  effectiveProjectId,
                  effectiveClientId,
                ),
              );
            }}
            onMicrosoftReauth={onStartSso}
            onMicrosoftRefreshObo={onRefreshMicrosoftObo}
          />
        );
      }
      return (
        <StepEditor
          stepNumber={2}
          assessmentLabel={assessmentLabel}
          onRunGenerate={() =>
            navigateTo(appRoutes.step2Generating(effectiveProjectId, effectiveClientId))
          }
          onOpenOutput={() =>
            navigateTo(appRoutes.step2Output(effectiveProjectId, effectiveClientId))
          }
        />
      );
    case 'step-2-generating':
      if (assessmentInstanceId) {
        const instanceId = assessmentInstanceId;
        return (
          <AssessmentStep2Generating
            assessmentInstanceId={instanceId}
            clientId={effectiveClientId}
            projectId={effectiveProjectId}
            accessToken={accessToken}
            clientLabel={clientLabel}
            transcriptCount={step2Counts.transcriptCount}
            documentCount={step2Counts.documentCount}
            onNavigateReview={() =>
              navigateTo(
                appRoutes.projectAssessmentStep2Output(
                  instanceId,
                  effectiveProjectId,
                  effectiveClientId,
                ),
              )
            }
            onBack={() =>
              navigateTo(
                appRoutes.projectAssessmentStep2(instanceId, effectiveProjectId, effectiveClientId),
              )
            }
          />
        );
      }
      return (
        <StepGenerating
          stepNumber={2}
          onOpenOutput={() =>
            navigateTo(appRoutes.step2Output(effectiveProjectId, effectiveClientId))
          }
        />
      );
    case 'step-2-output':
      if (assessmentInstanceId) {
        const instanceId = assessmentInstanceId;
        return (
          <AssessmentStep2Review
            assessmentInstanceId={instanceId}
            clientId={effectiveClientId}
            projectId={effectiveProjectId}
            accessToken={accessToken}
            clientLabel={clientLabel}
            onNavigateAssessmentStep={(step) => onNavigateAssessmentStep(instanceId, step)}
            onNavigateBack={() =>
              navigateTo(
                appRoutes.projectAssessmentStep2(instanceId, effectiveProjectId, effectiveClientId),
              )
            }
            onNavigateForward={() =>
              navigateTo(appRoutes.projectHome(effectiveClientId, effectiveProjectId))
            }
          />
        );
      }
      return (
        <StepOutput
          stepNumber={2}
          onBackToStep={() => navigateTo(appRoutes.step2(effectiveProjectId, effectiveClientId))}
          onContinue={() => navigateTo(appRoutes.step3(effectiveProjectId, effectiveClientId))}
        />
      );
    default:
      return null;
  }
};
